import {
  Component,
  ElementRef,
  OnInit,
  Optional,
  Renderer2,
  Self,
  ViewChild,
} from '@angular/core';
import { NgControl, ValidatorFn } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@ngneat/reactive-forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { User } from '@digital-platform/people/domain';
import { PeopleFacadeService } from '@digital-platform/people/services';

interface SalesRepInfo {
  name: string;
  number: string;
}

@Component({
  selector: 'digital-platform-sales-rep-drop-down',
  templateUrl: './sales-rep-drop-down.component.html',
  styleUrls: ['./sales-rep-drop-down.component.scss'],
})
export class SalesRepDropDownComponent
  implements OnInit, ControlValueAccessor<SalesRepInfo | null>
{
  @ViewChild('selectRepRef', { read: ElementRef }) selectRepRef!: ElementRef;

  salesReps$: Observable<SalesRepInfo[]>;
  control = new FormControl<SalesRepInfo | null>();
  public onTouched: () => void;

  private originalValidators: ValidatorFn[] = [];

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private facade: PeopleFacadeService,
    private renderer: Renderer2
  ) {
    if (controlDir !== null) {
      controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (this.controlDir !== null) {
      // ensures validators and validation state gets handled properly, feels a little hacky
      const control = this.controlDir.control;
      let myValidators = [this.validate];
      if (control.validator) {
        this.originalValidators.push(control.validator);
        myValidators = [control.validator, ...myValidators];
        this.control.setValidators(control.validator);
        this.control.updateValueAndValidity({ emitEvent: false });
      }
      control.setValidators(myValidators);
      control.updateValueAndValidity();
      const markAllAsTouched = control.markAllAsTouched.bind(control);
      control.markAllAsTouched = () => {
        markAllAsTouched();
        this.control.markAllAsTouched();
      };
    }

    this.salesReps$ = this.facade.salesReps$.pipe(
      map((reps) => this.getRepsNameAndNumber(reps)),
      map((reps) => this.addCurrentValueIfNotPresent(reps)),
      map((reps) => reps.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  compareOptions(option1: SalesRepInfo, option2: SalesRepInfo): boolean {
    // consider 2 sales reps equal if the names are the same, and if we have
    // a number for both, if the numbers match as well
    const nameEqual = option1?.name === option2?.name;
    const numberEqual = option1?.number === option2?.number;

    if ((!option1?.number || !option2?.number) && nameEqual) {
      return true;
    }

    return nameEqual && numberEqual;
  }

  writeValue(value: SalesRepInfo | null): void {
    if (value) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: SalesRepInfo | null) => void): void {
    this.control.valueChanges
      .pipe(map((value) => (value?.name ? value : null)))
      .subscribe(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // ensures validators and validation state gets handled properly, feels a little hacky
  validate: ValidatorFn = () => {
    this.control.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });

    if (this.control.valid) {
      return null;
    }

    return this.control.errors;
  };

  private getRepsNameAndNumber(reps: User[]): SalesRepInfo[] {
    return reps.map((rep) => {
      const name = rep.name;
      const numberObj = rep.systemIds.find(
        (idObj) =>
          typeof idObj !== 'string' && idObj.system === 'logbook.salesrepswId'
      );

      return {
        name,
        number:
          numberObj && typeof numberObj !== 'string'
            ? numberObj.id || null
            : null,
      };
    });
  }

  private addCurrentValueIfNotPresent(reps: SalesRepInfo[]): SalesRepInfo[] {
    const currentValue = this.control.value;

    if (currentValue && currentValue.name !== '') {
      const found = reps.find((rep) => this.compareOptions(rep, currentValue));
      if (!found) {
        reps.push(currentValue);
      }
    }

    return reps;
  }

  focus(index: number) {
    this.renderer.setAttribute(
      this.selectRepRef.nativeElement,
      'tabindex',
      index.toString()
    );
  }
}
